import React, { useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import styled from 'styled-components';

import { GUID, Keyword, PersonOrg } from '@ndvr/customer-api';

import { CopyTaxIdButton } from '@/components/CopyTaxIdButton';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import {
    BillingAddressFormSection,
    ComplianceRequirements,
    ContactInformationFormSection,
    FinancialPicture,
    MailingAddressFormSection,
    PersonAdvancedBlock,
    PersonFormSection,
    PrimaryAddressFormSection,
} from 'modules';
import { FetcherError, formatTaxId, useRequiredParams } from 'utils';
import {
    useAssociateKeyword,
    useCreateKeyword,
    useDisassociateKeyword,
    useKeywords,
    useKeywordsForPersonOrg,
} from 'views/admin/segmentation/keyword-queries';
import { PersonFormValues } from 'views/person/person-form-model';
import {
    AccordionSection,
    AccordionSectionKeys,
    Form,
    FormToggle,
    getFieldName,
    HorizontalLayout,
    SaveBar,
    Text,
    Title,
    VerticalLayout,
} from 'widgets';

import { getPersonFormDefaultValues } from './person-form-utils';
import { usePersonOrgProfile, usePersonOrgTaxId, useSavePersonProfile, useSetPersonOrgTaxId } from './person-queries';

const ACCORDION_MAX_WIDTH = '762px';

export const PersonDetailsTab = () => {
    const { personOrgGUID } = useRequiredParams(['personOrgGUID']);
    const { data: existingPerson, isLoading } = usePersonOrgProfile(personOrgGUID);
    const { mutate: savePersonProfile, isPending: saveInProgress } = useSavePersonProfile({
        personGUID: personOrgGUID,
    });

    const defaultValues: PersonFormValues = useMemo(() => {
        return existingPerson ? getPersonFormDefaultValues(existingPerson) : {};
    }, [existingPerson]);

    return (
        <Form onSubmit={savePersonProfile} defaultValues={defaultValues} isLoading={isLoading || saveInProgress}>
            <VerticalLayout $gap={16}>
                <BasicFormSection />
                <ContactFormSection />
                <KeywordFormSection />
                <FinancialFormSection />
                <ComplianceFormSection />
                <AdvancedFormSection />
            </VerticalLayout>
            <SaveBar />
        </Form>
    );
};

const BasicFormSection = () => {
    const { personOrgGUID } = useRequiredParams(['personOrgGUID']);
    const { data: existingPerson } = usePersonOrgProfile(personOrgGUID);
    return (
        <AccordionSection
            title="Basic"
            maxWidth={ACCORDION_MAX_WIDTH}
            helperText="4 Properties"
            accordionKey={AccordionSectionKeys.personDetailsBasic}
            $background="white"
        >
            <AccordionLayout $gap={24}>
                <StyledPersonFormSection title="" />
                <TaxIdDisplay
                    personOrgGUID={personOrgGUID}
                    isProvided={existingPerson?.personOrg.identifyingTaxIdProvided}
                />
            </AccordionLayout>
        </AccordionSection>
    );
};

const StyledPersonFormSection = styled(PersonFormSection)`
    padding: 0;
`;

const ContactFormSection = () => {
    const { watch } = useFormContext();
    const mailingAddress = watch(getFieldName('mailingAddress'));
    const billingAddress = watch(getFieldName('billingAddress'));
    const numberOfFields = 9 + (mailingAddress?.sameAsPrimary ? 0 : 5) + (billingAddress?.sameAsPrimary ? 0 : 5);

    return (
        <AccordionSection
            title="Contact"
            maxWidth={ACCORDION_MAX_WIDTH}
            helperText={numberOfFields + ' Properties'}
            accordionKey={AccordionSectionKeys.personDetailsContact}
            $background="white"
        >
            <AccordionLayout $gap={32}>
                <StyledContactInformationFormSection />
                <StyledPrimaryAddressFormSection />
                <StyledMailingAddressFormSection />
                <StyledBillingAddressFormSection />
            </AccordionLayout>
        </AccordionSection>
    );
};

const StyledContactInformationFormSection = styled(ContactInformationFormSection)`
    padding: 0;
`;

const StyledPrimaryAddressFormSection = styled(PrimaryAddressFormSection)`
    padding: 0;
`;

const StyledMailingAddressFormSection = styled(MailingAddressFormSection)`
    padding: 0;
`;

const StyledBillingAddressFormSection = styled(BillingAddressFormSection)`
    padding: 0;
`;

const KeywordFormSection = () => {
    const { personOrgGUID } = useRequiredParams(['personOrgGUID']);
    const { data: personKeywords = [] } = useKeywordsForPersonOrg(personOrgGUID);
    const { data: allKeywords = [] } = useKeywords();
    const associateKeywordMutation = useAssociateKeyword();
    const disassociateKeywordMutation = useDisassociateKeyword();
    const createKeywordMutation = useCreateKeyword();
    const [isAddingKeyword, setIsAddingKeyword] = useState(false);
    const [isCreatingKeyword, setIsCreatingKeyword] = useState(false);
    const [newKeywordLabel, setNewKeywordLabel] = useState('');

    const availableKeywordOptions = allKeywords
        .filter((keyword) => !personKeywords.some((pk) => pk.keywordGUID === keyword.keywordGUID))
        .map((keyword) => ({
            value: keyword.keywordGUID,
            label: keyword.label,
        }));

    const handleAddKeyword = async (keywordGUID: string) => {
        if (keywordGUID) {
            await associateKeywordMutation.mutateAsync({
                personOrgGUID,
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                keywordGUID: keywordGUID as GUID<Keyword>,
            });
            setIsAddingKeyword(false);
        }
    };

    const handleRemoveKeyword = async (keywordGUID: string) => {
        await disassociateKeywordMutation.mutateAsync({
            personOrgGUID,
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            keywordGUID: keywordGUID as GUID<Keyword>,
        });
    };

    const handleCreateKeyword = async () => {
        if (newKeywordLabel.trim()) {
            const keywordGUID = await createKeywordMutation.mutateAsync({ label: newKeywordLabel.trim() });
            if (keywordGUID) {
                await associateKeywordMutation.mutateAsync({
                    personOrgGUID,
                    keywordGUID,
                });
            }
            setNewKeywordLabel('');
            setIsCreatingKeyword(false);
            setIsAddingKeyword(false);
        }
    };

    return (
        <AccordionSection
            title="Segmentation Keywords"
            maxWidth={ACCORDION_MAX_WIDTH}
            helperText={`${personKeywords.length} ${personKeywords.length === 1 ? 'Keyword' : 'Keywords'}`}
            accordionKey={AccordionSectionKeys.personDetailsKeywords}
            $background="white"
        >
            <AccordionLayout $gap={24}>
                <VerticalLayout $gap={12}>
                    {personKeywords.length > 0 && (
                        <KeywordList>
                            {personKeywords.map((keyword) => (
                                <KeywordTag key={keyword.keywordGUID}>
                                    <span>{keyword.label}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveKeyword(keyword.keywordGUID)}
                                        className="remove-btn"
                                    >
                                        <LuTrash2 className="h-3 w-3" />
                                    </button>
                                </KeywordTag>
                            ))}
                        </KeywordList>
                    )}
                    {!isAddingKeyword && (
                        <Button type="button" size="sm" onClick={() => setIsAddingKeyword(true)} variant="outline">
                            <LuPlus className="mr-1 h-4 w-4" />
                            Add Keyword
                        </Button>
                    )}
                    {isAddingKeyword && !isCreatingKeyword && (
                        <HorizontalLayout $gap={8}>
                            <div style={{ flex: 1 }}>
                                <Combobox
                                    options={availableKeywordOptions}
                                    placeholder="Select a keyword..."
                                    noOptionsLabel="No keywords available"
                                    onValueChange={handleAddKeyword}
                                    customActionLabel="Create new keyword"
                                    onCustomAction={() => setIsCreatingKeyword(true)}
                                />
                            </div>
                            <Button type="button" size="sm" variant="ghost" onClick={() => setIsAddingKeyword(false)}>
                                Cancel
                            </Button>
                        </HorizontalLayout>
                    )}
                    {isCreatingKeyword && (
                        <HorizontalLayout $gap={8}>
                            <div style={{ flex: 1 }}>
                                <Input
                                    value={newKeywordLabel}
                                    onChange={(e) => setNewKeywordLabel(e.target.value)}
                                    placeholder="Enter keyword name..."
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleCreateKeyword();
                                        if (e.key === 'Escape') {
                                            setIsCreatingKeyword(false);
                                            setNewKeywordLabel('');
                                        }
                                    }}
                                />
                            </div>
                            <Button type="button" size="sm" onClick={handleCreateKeyword}>
                                Create
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setIsCreatingKeyword(false);
                                    setNewKeywordLabel('');
                                }}
                            >
                                Cancel
                            </Button>
                        </HorizontalLayout>
                    )}
                </VerticalLayout>
            </AccordionLayout>
        </AccordionSection>
    );
};

const FinancialFormSection = () => {
    return (
        <AccordionSection
            title="Financial Picture"
            maxWidth={ACCORDION_MAX_WIDTH}
            helperText="5 Properties"
            accordionKey={AccordionSectionKeys.personDetailsFinancial}
            $background="white"
        >
            <AccordionLayout>
                <FinancialPicture />
            </AccordionLayout>
        </AccordionSection>
    );
};

const ComplianceFormSection = () => {
    return (
        <AccordionSection
            title="Compliance"
            maxWidth={ACCORDION_MAX_WIDTH}
            helperText="5 Properties"
            accordionKey={AccordionSectionKeys.personDetailsCompliance}
            $background="white"
        >
            <AccordionLayout $gap={24}>
                <ComplianceRequirements />
                <FormToggle name={getFieldName('ndvrAffiliated')} label="Claro Affiliated" />
            </AccordionLayout>
        </AccordionSection>
    );
};

const AdvancedFormSection = () => {
    return (
        <AccordionSection
            title="Advanced"
            maxWidth={ACCORDION_MAX_WIDTH}
            helperText="2 Properties"
            accordionKey={AccordionSectionKeys.personDetailsAdvanced}
            $background="white"
        >
            <AccordionLayout $gap={24}>
                <PersonAdvancedBlock />
            </AccordionLayout>
        </AccordionSection>
    );
};

const isForbiddenError = (error: unknown) => error instanceof FetcherError && error.httpStatus === 403;

const TaxIdDisplay = ({ personOrgGUID, isProvided }: { personOrgGUID: GUID<PersonOrg>; isProvided?: boolean }) => {
    const { data, refetch, isFetching } = usePersonOrgTaxId(personOrgGUID);
    const { mutate: setTaxId, isPending } = useSetPersonOrgTaxId(personOrgGUID);
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [savedValue, setSavedValue] = useState<string | undefined>(undefined);
    const [forbidden, setForbidden] = useState(false);
    const [validationError, setValidationError] = useState<string | undefined>(undefined);
    const inputRef = useRef<HTMLInputElement>(null);

    const isActuallyProvided = isProvided || savedValue !== undefined;
    const displayValue = savedValue ?? data?.plainText;

    const handleShow = async () => {
        const result = await refetch();
        if (isForbiddenError(result.error)) {
            setForbidden(true);
        }
    };

    const handleEdit = async () => {
        let value = displayValue;
        if (!value) {
            const result = await refetch();
            if (isForbiddenError(result.error)) {
                setForbidden(true);
                return;
            }
            value = result.data?.plainText;
        }
        setInputValue(value ?? '');
        setIsEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSave = () => {
        const stripped = inputValue.replace(/-/g, '');
        if (!/^\d{9}$/.test(stripped)) {
            setValidationError('Tax ID must be 9 digits');
            return;
        }
        setValidationError(undefined);
        setTaxId(stripped, {
            onSuccess: () => {
                setSavedValue(stripped);
                setIsEditing(false);
            },
        });
    };

    const handleCancel = () => {
        setIsEditing(false);
        setInputValue('');
        setValidationError(undefined);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleSave();
        if (e.key === 'Escape') handleCancel();
    };

    return (
        <HorizontalLayout $rowCenter $gap={32}>
            <Title content>Tax ID</Title>
            {forbidden ? (
                <Text className="font-medium text-red-500">No access</Text>
            ) : isEditing ? (
                <HorizontalLayout $gap={8} $rowCenter>
                    <div className="flex flex-col gap-1">
                        <input
                            ref={inputRef}
                            className={`w-32 rounded border px-2 py-1 text-sm ${
                                validationError ? 'border-red-500' : 'border-gray-300'
                            }`}
                            value={inputValue}
                            onChange={(e) => {
                                setInputValue(e.target.value);
                                setValidationError(undefined);
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder="XXX-XX-XXXX"
                            disabled={isPending}
                        />
                        {validationError && <span className="text-xs text-red-500">{validationError}</span>}
                    </div>
                    <Button type="button" onClick={handleSave} size="sm" disabled={isPending}>
                        {isPending ? 'Saving...' : 'Save'}
                    </Button>
                    <Button type="button" onClick={handleCancel} size="sm" variant="outline" disabled={isPending}>
                        Cancel
                    </Button>
                </HorizontalLayout>
            ) : isActuallyProvided ? (
                <HorizontalLayout $gap={8} $rowCenter>
                    {displayValue ? (
                        <>
                            <Text>{formatTaxId(displayValue)}</Text>
                            <CopyTaxIdButton value={displayValue} />
                        </>
                    ) : (
                        <Button type="button" onClick={handleShow} size="sm" disabled={isFetching}>
                            Show
                        </Button>
                    )}
                    <Button type="button" onClick={handleEdit} size="sm" variant="outline" disabled={isFetching}>
                        Edit
                    </Button>
                </HorizontalLayout>
            ) : (
                <HorizontalLayout $gap={8} $rowCenter>
                    <Text>Not provided</Text>
                    <Button type="button" onClick={handleEdit} size="sm" variant="outline">
                        Set
                    </Button>
                </HorizontalLayout>
            )}
        </HorizontalLayout>
    );
};

const AccordionLayout = styled(VerticalLayout)`
    padding-right: 120px;
`;

const KeywordList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const KeywordTag = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 16px;
    border: 1px solid var(--espresso-200, #e5e5e5);
    background: var(--seafoam, #f5f5f5);
    font-size: 14px;
    color: var(--espresso-600, #333);

    .remove-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2px;
        border: none;
        background: transparent;
        color: var(--espresso-400, #999);
        cursor: pointer;
        border-radius: 4px;
        transition: all 0.2s;

        &:hover {
            color: var(--destructive, #ef4444);
            background: var(--destructive-light, #fee);
        }
    }
`;
